import { ApiError, type Campaign, type CampaignStatus, type ScheduledPost } from '../../lib/api';
import { StatusBadge, type Status } from '../../components/ui';
import i18n from '../../i18n';
import { formatDayRange } from '../../lib/format';
import { errorMessage } from '../contacts/shared';

/** What the campaign pages share (R27, R28: `Campaigns.dc.html`, `R28-CampaignDetail.dc.html`; D-094, D-129). */

/** A campaign's state in the glossary's words (GLOSSARY-FR §2): "In progress" becomes Active, a stopped one Arrêtée (D-197). */
const STATUS: Record<CampaignStatus, Status> = {
  draft: 'draft',
  active: 'active',
  paused: 'paused',
  completed: 'completed',
  canceled: 'stopped',
};

/** The glossary status of a campaign state: its word is `status.<it>` (the calendar's chips, D-188). */
export const campaignStatusOf = (status: CampaignStatus): Status => STATUS[status];

export function CampaignStatusBadge({ status }: { status: CampaignStatus }) {
  return <StatusBadge status={STATUS[status]} />;
}

/** When a post went or goes out: its publication, else its (planned) time. */
export function postDate(post: ScheduledPost): Date | null {
  const iso = post.publishedAt ?? post.scheduledAt;
  return iso ? new Date(iso) : null;
}

/** Planned posts that launching would refuse for want of a time (QUESTIONS Q2). */
export function untimedCount(posts: ScheduledPost[]): number {
  return posts.filter((p) => p.status === 'draft' && !p.scheduledAt).length;
}

/** "15 mars 2027", "15–28 mars 2027" in the scheduling zone (D-146); null when no post has a time yet. */
export function dateRange(posts: ScheduledPost[], zone: string): string | null {
  const times = posts
    .map(postDate)
    .filter((d): d is Date => d !== null)
    .map((d) => d.getTime());
  if (times.length === 0) return null;
  return formatDayRange(Math.min(...times), Math.max(...times), { timeZone: zone });
}

/** The account the posts go out on, or how many when they are several. */
export function accountsLabel(posts: ScheduledPost[]): string | null {
  const names = [...new Set(posts.map((p) => p.channelAccount.displayName))];
  if (names.length === 0) return null;
  return names.length === 1 ? names[0]! : i18n.t('campaigns.meta.accounts', { count: names.length });
}

/**
 * "4 publications · 14–28 oct. 2027 · @acme.marketing", or "2 publications · 2 comptes ·
 * pas encore d'horaire" (R27). The dates and account only once the posts are known, the
 * dates once the scheduling zone is too; the detail leaves out "no times yet", which its
 * pre-launch alert already says.
 */
export function campaignMeta(total: number, posts: ScheduledPost[] | null, zone: string | null, options: { noDates?: boolean } = {}): string {
  if (total === 0) return i18n.t('campaigns.meta.none');
  // "2 publications · 1 story" once the posts are known; the list only has the total (D-137).
  const stories = posts?.filter((p) => p.type === 'story').length ?? 0;
  const parts = [i18n.t('campaigns.meta.posts', { count: total - stories })];
  if (stories > 0) parts.push(i18n.t('campaigns.meta.stories', { count: stories }));
  if (posts) {
    const range = zone ? dateRange(posts, zone) : null;
    if (range) parts.push(range);
    const accounts = accountsLabel(posts);
    if (accounts) parts.push(accounts);
    if (zone && !range && options.noDates !== false) parts.push(i18n.t('campaigns.meta.noDates'));
  }
  return parts.join(' · ');
}

/** Published out of everything that can still or did publish (canceled posts excluded); shown once launched (A11-03). */
export function CampaignProgress({ campaign }: { campaign: Campaign }) {
  const { counts } = campaign;
  const relevant = counts.total - counts.canceled;
  const share = relevant > 0 ? counts.published / relevant : 0;
  return (
    <div className="flex items-center gap-3">
      <div
        className="h-1.5 max-w-[340px] flex-1 overflow-hidden rounded-full bg-surface-3"
        role="progressbar"
        aria-valuenow={Math.round(share * 100)}
        aria-label={i18n.t('campaigns.row.progressLabel')}
      >
        <div className={`h-full rounded-full ${campaign.status === 'completed' ? 'bg-success-700' : 'bg-primary-600'}`} style={{ width: `${share * 100}%` }} />
      </div>
      <p className="tabular shrink-0 text-ds-xs text-ink-2">
        {i18n.t('campaigns.row.progress', { published: counts.published, total: relevant })}
        {counts.failed > 0 ? <span className="text-danger-700"> · {i18n.t('campaigns.row.failed', { count: counts.failed })}</span> : null}
      </p>
    </div>
  );
}

/**
 * The issues a refused launch returns, so every one can be fixed at once. A
 * stable code gets the web's own words (QUESTIONS Q8 a); the server's
 * per-post issues are free text and stay as sent.
 */
export function launchIssues(err: unknown, emptyText: string): string[] {
  if (err instanceof ApiError && err.code === 'CAMPAIGN_EMPTY') return [emptyText];
  if (err instanceof ApiError && Array.isArray(err.details?.issues)) return err.details.issues as string[];
  return [errorMessage(err)];
}
